import { useState, useEffect, useRef } from 'react'
import { Input } from '#app/components/ui/input'
import {
	MultipleComboBox,
	type MultipleComboBoxItem,
} from '#app/components/ui/multiple-combo-box'
import { cn, useDebounce } from '#app/utils/misc'

interface FilterOption {
	id: string
	name: string
}

export interface GrantSearchFilterValues {
	territories?: string[]
	fundingAreas?: string[]
	fundingTypes?: string[]
	grantName?: string
}

interface GrantSearchFilterProps {
	fundingAreas: FilterOption[]
	territories: FilterOption[]
	fundingTypes: FilterOption[]
	initialValues: GrantSearchFilterValues
	onFilterChange: (filters: GrantSearchFilterValues) => void
	disableGrantName?: boolean
}

export function GrantSearchFilter({
	fundingAreas,
	territories,
	fundingTypes,
	initialValues,
	onFilterChange,
	disableGrantName,
}: GrantSearchFilterProps) {
	const [selectedTerritories, setSelectedTerritories] = useState<
		MultipleComboBoxItem[]
	>([])
	const [selectedFundingAreas, setSelectedFundingAreas] = useState<
		MultipleComboBoxItem[]
	>([])
	const [selectedFundingTypes, setSelectedFundingTypes] = useState<
		MultipleComboBoxItem[]
	>([])
	const [grantName, setGrantName] = useState<string>(
		initialValues.grantName || '',
	)

	// Ref to store the previous grant name for comparison
	const prevGrantNameRef = useRef<string | undefined>(initialValues.grantName)

	// Helper function to convert IDs to items
	const getItemsFromIds = (
		ids: string[] | undefined,
		options: FilterOption[],
	): MultipleComboBoxItem[] => {
		if (!ids || ids.length === 0) return []
		return options.filter(option => ids.includes(option.id))
	}

	// Helper function to convert items to IDs
	const getIdsFromItems = (items: MultipleComboBoxItem[]): string[] => {
		return items.map(item => item.id)
	}

	useEffect(() => {
		setSelectedTerritories(
			getItemsFromIds(initialValues.territories, territories),
		)
		setSelectedFundingAreas(
			getItemsFromIds(initialValues.fundingAreas, fundingAreas),
		)
		setSelectedFundingTypes(
			getItemsFromIds(initialValues.fundingTypes, fundingTypes),
		)
	}, [initialValues, territories, fundingAreas, fundingTypes])

	useEffect(
		function updateGrantName() {
			// Only update state if the grant name has actually changed
			if (initialValues.grantName !== prevGrantNameRef.current) {
				prevGrantNameRef.current = initialValues.grantName
				setGrantName(initialValues.grantName || '')
			}
		},
		[initialValues.grantName],
	)

	const handleFilterUpdate = (
		newTerritories?: MultipleComboBoxItem[],
		newFundingAreas?: MultipleComboBoxItem[],
		newFundingTypes?: MultipleComboBoxItem[],
		newGrantName?: string,
	) => {
		const territoriesToUse =
			newTerritories !== undefined ? newTerritories : selectedTerritories
		const fundingAreasToUse =
			newFundingAreas !== undefined ? newFundingAreas : selectedFundingAreas
		const fundingTypesToUse =
			newFundingTypes !== undefined ? newFundingTypes : selectedFundingTypes
		const grantNameToUse = newGrantName !== undefined ? newGrantName : grantName

		onFilterChange({
			territories: getIdsFromItems(territoriesToUse),
			fundingAreas: getIdsFromItems(fundingAreasToUse),
			fundingTypes: getIdsFromItems(fundingTypesToUse),
			grantName: disableGrantName ? undefined : grantNameToUse || undefined,
		})
	}

	// Debounced version of handleFilterUpdate for grant name changes
	const debouncedGrantNameUpdate = useDebounce((newGrantName: string) => {
		handleFilterUpdate(undefined, undefined, undefined, newGrantName)
	}, 300)

	return (
		<div className="my-2 flex flex-col gap-2">
			<div>
				<label htmlFor="fundingAreaSelect" className="text-sm font-semibold">
					Funding Area
				</label>
				<MultipleComboBox
					items={fundingAreas}
					selectedItems={selectedFundingAreas}
					onSelectionChange={newSelection => {
						setSelectedFundingAreas(newSelection)
						handleFilterUpdate(undefined, newSelection, undefined, undefined)
					}}
					placeholder="All Funding Areas"
					searchPlaceholder="Search funding areas..."
					className="w-full bg-white"
				/>
			</div>

			<div>
				<label htmlFor="territorySelect" className="text-sm font-semibold">
					Territory
				</label>
				<MultipleComboBox
					items={territories}
					selectedItems={selectedTerritories}
					onSelectionChange={newSelection => {
						setSelectedTerritories(newSelection)
						handleFilterUpdate(newSelection, undefined, undefined, undefined)
					}}
					placeholder="All Territories"
					searchPlaceholder="Search territories..."
					className="w-full bg-white"
				/>
			</div>

			<div>
				<label htmlFor="fundingTypeSelect" className="text-sm font-semibold">
					Funding Type
				</label>
				<MultipleComboBox
					items={fundingTypes}
					selectedItems={selectedFundingTypes}
					onSelectionChange={newSelection => {
						setSelectedFundingTypes(newSelection)
						handleFilterUpdate(undefined, undefined, newSelection, undefined)
					}}
					placeholder="All Funding Types"
					searchPlaceholder="Search funding types..."
					className="w-full bg-white"
				/>
			</div>

			{!disableGrantName && (
				<div>
					<label htmlFor="grantNameSearch" className="text-sm font-semibold">
						Grant Name
					</label>
					<Input
						type="text"
						id="grantNameSearch"
						className={cn(
							grantName && 'border-brand-cyan-8',
							'w-full rounded-md border p-2',
						)}
						value={grantName}
						placeholder="Search grant name"
						onChange={e => {
							const newGrantName = e.target.value
							setGrantName(newGrantName)
							debouncedGrantNameUpdate(newGrantName)
						}}
					/>
				</div>
			)}
		</div>
	)
}
